import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import useAppManager, {
  AppCode,
  getCookie,
  Language,
  useFetch,
} from '@mindbaz/app-manager';
import { Loader } from '@mindbaz/ui-v2';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { activateLocale } from './i18n';
import PrivateRoutes from './pages/PrivateRoutes';
import PublicRoutes, { publicPages } from './pages/PublicRoutes';

const { VITE_API_ENV, VITE_APPLICATION_ID } = import.meta.env;

if (VITE_API_ENV !== 'production') document.title += ' PREPROD';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      suspense: true,
    },
  },
});

const defaultValues = {
  lastConnexionIdSite: -1,
  lastConnexionLanguage: 'fr',
};

const App = () => {
  const isPublicPage = publicPages.includes(window.location.pathname);

  const [status] = useAppManager({
    appId: parseInt(VITE_APPLICATION_ID, 10),
    appCode: AppCode.SUPERSET,
    env: import.meta.env.VITE_API_ENV === 'production' ? 'prod' : 'preprod',
    disableTokenProvider: isPublicPage,
  });

  const [preferencesStatus, preferencesResult]: any = useFetch({
    method: 'GET',
    url: `${import.meta.env.VITE_SSO_URL}api/user/preferences`,
  });

  if (preferencesStatus !== 'success') return <Loader />;

  const { lastConnexionLanguage } = preferencesResult.data || defaultValues;

  const cookieLang = getCookie('mbzLang') as Language;

  activateLocale(
    lastConnexionLanguage.toLowerCase() || cookieLang.toLowerCase()
  );

  if (status !== 'success' && status !== 'error') return <Loader />;
  if (status === 'error') {
    // TODO: redirect to 403
  }
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider i18n={i18n}>
        <ReactQueryDevtools initialIsOpen={false} />
        {status === 'success' && isPublicPage && <PublicRoutes />}
        {status === 'success' && !isPublicPage && <PrivateRoutes />}
      </I18nProvider>
    </QueryClientProvider>
  );
};

export default Sentry.withProfiler(App);
